import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Button } from '../ui/Button';
import { Container } from '../ui/Container';
import { ArrowRight, Shield } from 'lucide-react';
export function Hero() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        const interval = setInterval(() => {
          setProgress(prev => {
            if (prev >= 60) {
              clearInterval(interval);
              return 60;
            }
            return prev + 1;
          });
        }, 20);
        return () => clearInterval(interval);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isInView]);
  return <section className="relative overflow-hidden bg-dashboard-bg pt-32 pb-20 lg:pt-40 lg:pb-28 border-b border-dashboard-border" data-id="element-141">
      <Container className="relative z-10" data-id="element-142">
        <div className="mx-auto max-w-5xl" data-id="element-143">
          {/* Trust bar */}
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-wrap items-center justify-center gap-4 mb-12 text-sm text-dashboard-muted" data-id="element-144">
            <div className="flex items-center gap-2" data-id="element-145">
              <Shield className="w-4 h-4 text-tech-blue" data-id="element-146" />
              <span data-id="element-147">Microsoft Solutions Partner</span>
            </div>
            <span className="hidden sm:inline text-dashboard-border" data-id="element-148">|</span>
            <span data-id="element-149">Serving Perth & Australia-wide</span>
            <span className="hidden sm:inline text-dashboard-border" data-id="element-150">|</span>
            <span data-id="element-151">No new software required</span>
          </motion.div>

          {/* Main content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-center" data-id="element-152">
            {/* Headline */}
            <h1 className="text-4xl font-black tracking-tight text-dashboard-text sm:text-5xl lg:text-6xl mb-6 leading-tight" data-id="element-153">
              Stop Wasting{' '}
              <span className="text-tech-blue" data-id="element-154">60% of Project Time</span> on
              Admin.
            </h1>

            {/* Subheadline */}
            <p className="text-xl leading-relaxed text-dashboard-muted max-w-3xl mx-auto mb-10" data-id="element-155">
              We integrate and automate your existing Microsoft systems to help
              engineering teams deliver projects on time, under budget, and with
              fewer mistakes.
            </p>

            {/* Progress bar */}
            <div ref={ref} className="max-w-2xl mx-auto mb-12" data-id="element-156">
              <div className="bg-dashboard-surface rounded-lg p-6 border border-dashboard-border" data-id="element-157">
                <div className="flex items-center justify-between mb-3" data-id="element-158">
                  <span className="text-sm font-semibold text-dashboard-text" data-id="element-159">
                    Current admin overhead
                  </span>
                  <span className="text-3xl font-black text-tech-red" data-id="element-160">
                    {progress}%
                  </span>
                </div>
                <div className="relative h-2 bg-dashboard-bg rounded-full overflow-hidden" data-id="element-161">
                  <motion.div className="absolute inset-y-0 left-0 bg-tech-red rounded-full" initial={{
                  width: 0
                }} animate={{
                  width: `${progress}%`
                }} transition={{
                  duration: 2,
                  ease: 'easeOut'
                }} data-id="element-162" />
                </div>
                <div className="mt-3 text-sm text-dashboard-muted" data-id="element-163">
                  Target with automation:{' '}
                  <span className="text-tech-green font-semibold" data-id="element-164">15%</span>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8" data-id="element-165">
              <Button variant="cta" size="xl" className="w-full sm:w-auto group" data-id="element-166">
                Book a Discovery Call
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-167" />
              </Button>
              <a href="https://ai-readiness.laisolutions.com.au/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-tech-blue bg-tech-blue/10 hover:bg-tech-blue/20 border border-tech-blue/30 hover:border-tech-blue rounded-lg transition-all" data-id="element-168">
                Or take our AI Readiness Assessment →
              </a>
            </div>

            {/* Reassurance */}
            <p className="text-sm text-dashboard-muted" data-id="element-169">
              30-minute consultation • No obligation • No sales pressure
            </p>
          </motion.div>

          {/* Client logos */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6,
          duration: 0.6
        }} className="mt-20 pt-12 border-t border-dashboard-border" data-id="element-170">
            <p className="text-xs text-dashboard-muted mb-8 uppercase tracking-wider text-center" data-id="element-171">
              Trusted by engineers from
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-172">
              {['Fortescue', 'Mineral Resources', 'ECG Engineering', 'Grid Construction'].map((company, index) => <motion.div key={company} className="text-center py-4 px-6 bg-dashboard-surface rounded border border-dashboard-border" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7 + index * 0.1,
              duration: 0.5
            }} data-id="element-173">
                  <div className="text-sm font-bold text-dashboard-text" data-id="element-174">
                    {company}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </Container>
    </section>;
}